/**
 * PartnerSettlementHistory Component
 * 협력사 주정산내역 페이지 (본사용/협력사용)
 */
import React, {useCallback, useEffect, useRef, useState} from "react";
import {usePartnerSettlementData} from "../hooks/usePartnerSettlementData";
import {usePartnerSettlementHandlers} from "../hooks/usePartnerSettlementHandlers";
import {useDelayedLoading} from "@/shared/hooks/useDelayedLoading";
import {useUserInfo} from "@/shared/hooks/useCrudOperations";
import {usePagination} from "@/shared/hooks/usePagination";
import {useTableSort} from "@/shared/hooks/useTableSort";
import {loadFilters, saveFilters} from "@/shared/utils/filterStorage";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";
import {PartnerSettlement} from "../types/partnerSettlement.types";
import FilterSection from './FilterSection';
import SettlementTableRow from './SettlementTableRow';
import TableHeader from './TableHeader';
import Pagination from '@/shared/components/dataDisplay/Pagination';
import Loading from '@/shared/components/feedback/Loading';
import '@/shared/styles/common-table.css';
import '../styles/PartnerSettlementHistory.css';

/** 페이지당 아이템 수 */
const ITEMS_PER_PAGE = { DEFAULT: 10 };

const PartnerSettlementHistory: React.FC = () => {
    // ========================================
    // 1. 상수 정의
    // ========================================
    const FILTER_STORAGE_KEY = 'partnerSettlementFilters';
    const INITIAL_LOADING_DELAY = 1500; // 초기 로딩 지연 시간 (1.5초)

    // ========================================
    // 2. Hooks - 사용자 정보 및 권한
    // ========================================
    const { showToast } = useToast();
    const { getUserInfo } = useUserInfo();
    const userInfo = getUserInfo();
    const isSupplierUser = userInfo?.userType === 'SUPPLIER';
    const userSupplierId = isSupplierUser ? userInfo?.userId : null;

    // ========================================
    // 3. Hooks - 초기 데이터 로드 여부 결정
    // ========================================
    // 저장된 필터가 있으면 초기 조회 건너뛰기 (저장된 필터로 조회 예정)
    const skipInitialFetch = React.useMemo(() => {
        const saved = loadFilters<{ acctStDate: string; acctEdDate: string }>(FILTER_STORAGE_KEY);
        return !!(saved?.acctStDate && saved?.acctEdDate);
    }, []);

    // ========================================
    // 4. Hooks - 데이터 관리
    // ========================================
    const {
        settlements,
        suppliers,
        latestStartDate,
        latestEndDate,
        loading,
        refreshSettlements,
        downloadExcel
    } = usePartnerSettlementData(userSupplierId, skipInitialFetch);

    const showLoading = useDelayedLoading(loading);

    // 초기 로딩 상태: 1.5초 타이머 완료 여부와 데이터 로드 완료 여부를 모두 확인
    const [minLoadingTimeElapsed, setMinLoadingTimeElapsed] = useState(false);
    const [initialDataLoaded, setInitialDataLoaded] = useState(false);

    // ========================================
    // 5. State - 로컬 상태 관리
    // ========================================
    // 필터 상태 (sessionStorage에서 복원)
    const [filters, setFilters] = useState(() => {
        const saved = loadFilters<{
            acctStDate: string;
            acctEdDate: string;
            suppId: string;
        }>(FILTER_STORAGE_KEY);

        // 저장된 suppId가 'ALL'이면 빈 문자열로 변환
        const savedSuppId = saved?.suppId === 'ALL' ? '' : (saved?.suppId || '');
        return saved ? {
            acctStDate: saved.acctStDate || '',
            acctEdDate: saved.acctEdDate || '',
            suppId: isSupplierUser ? (userSupplierId || '') : savedSuppId
        } : {
            acctStDate: '',
            acctEdDate: '',
            suppId: isSupplierUser ? (userSupplierId || '') : ''
        };
    });

    // 초기 필터 적용 여부 추적
    const hasAppliedInitialFilter = useRef(false);

    // 정렬 훅 사용
    const {
        sortConfig,
        sortedData: sortedSettlements,
        handleSort
    } = useTableSort<PartnerSettlement>({
        data: settlements || [],
        numericColumns: ['processCount', 'deliveryFee', 'additionalSettlement', 'managementFee', 'supplyAmount', 'vat', 'supplyPrice', 'branchOperationFee', 'achievementPayback', 'promotion', 'paymentManagementFee', 'dailyDeduction', 'employerEmployment', 'employerIndustrial', 'insuranceTotal', 'actualPayment'],
        dateColumns: ['settlementStartDate', 'settlementEndDate', 'paymentDate']
    });

    // ========================================
    // 6. Hooks - 페이지네이션 (정렬된 데이터 기준)
    // ========================================
    const {
        currentPage,
        totalPages,
        currentItems,
        goToPage,
        resetPagination
    } = usePagination({ items: sortedSettlements, itemsPerPage: ITEMS_PER_PAGE.DEFAULT });

    // ========================================
    // 7. Hooks - 핸들러 함수
    // ========================================
    const {
        handleSearch,
        handleExcelDownload
    } = usePartnerSettlementHandlers({
        filters,
        setFilters,
        refreshSettlements,
        downloadExcel,
        resetPagination
    });

    /**
     * 필터 초기화 핸들러
     */
    const handleReset = useCallback(() => {
        // 최근 정산일자로 초기화
        setFilters({
            acctStDate: latestStartDate || '',
            acctEdDate: latestEndDate || '',
            suppId: isSupplierUser ? (userSupplierId || '') : ''
        });
    }, [latestStartDate, latestEndDate, isSupplierUser, userSupplierId]);

    // ========================================
    // 8. Side Effects - 데이터 로드 및 동기화
    // ========================================
    // 페이지 진입 시 최소 1.5초간 로딩 표시
    useEffect(() => {
        const timer = setTimeout(() => {
            setMinLoadingTimeElapsed(true);
        }, INITIAL_LOADING_DELAY);

        return () => clearTimeout(timer);
    }, []);

    // 초기 데이터 로드 완료 감지 (settlements에 데이터가 들어오면 완료)
    useEffect(() => {
        // loading이 false가 되면 (데이터 유무와 관계없이) 로드 완료로 처리
        if (!loading && latestStartDate !== undefined && latestEndDate !== undefined) {
            setInitialDataLoaded(true);
        }
    }, [loading, latestStartDate, latestEndDate]);

    // 필터 변경 시 sessionStorage에 저장
    useEffect(() => {
        saveFilters(FILTER_STORAGE_KEY, filters);
    }, [filters]);

    // 최초 로딩 시 최근 정산일자 설정 및 데이터 조회
    useEffect(() => {
        if (hasAppliedInitialFilter.current) return;

        if (latestStartDate && latestEndDate && !loading) {
            // 저장된 필터가 없으면 최근 정산시작일자와 종료일자로 설정
            if (!filters.acctStDate || !filters.acctEdDate) {
                setFilters(prev => ({ ...prev, acctStDate: latestStartDate, acctEdDate: latestEndDate }));
            }

            // 저장된 필터가 있으면 해당 필터로 조회
            if (skipInitialFetch) {
                const saved = loadFilters<typeof filters>(FILTER_STORAGE_KEY);
                if (saved?.acctStDate && saved?.acctEdDate) {
                    refreshSettlements(
                        saved.acctStDate,
                        saved.acctEdDate,
                        saved.suppId === 'ALL' ? undefined : saved.suppId
                    );
                }
            }

            hasAppliedInitialFilter.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [latestStartDate, latestEndDate, loading, skipInitialFetch]);


    // ========================================
    // 9. Render - 로딩 상태
    // ========================================
    // 초기 로딩: 1.5초 타이머 완료 AND 데이터 로드 완료 시에만 로딩 해제
    const isInitialLoading = !minLoadingTimeElapsed || !initialDataLoaded;
    
    if (loading && !isInitialLoading) {
        return <Loading message="엑셀 다운로드 중..." />;
    }
    
    if (isInitialLoading) {
        return <Loading message="협력사 주정산내역을 불러오는 중..." />;
    }

    // ========================================
    // 10. Render - 메인 UI
    // ========================================
    return (
        <div className="supplier-selection-page">
            {/* 페이지 제목 */}
            <div className="page-title-wrapper">
                <h1 className="page-title">협력사 주정산내역</h1>
            </div>

            {/* 필터 섹션 */}
            <FilterSection
                filters={filters}
                suppliers={suppliers}
                isSupplierUser={isSupplierUser}
                onFilterChange={setFilters}
                onSearch={handleSearch}
                onReset={handleReset}
                onExcelDownload={handleExcelDownload}
            />

            {/* 검색 결과 건수 */}
            <div className="result-count">
                <i className="fas fa-list"></i>
                총 <strong>{sortedSettlements.length.toLocaleString()}건</strong>
            </div>

            {/* 테이블 섹션 */}
            <div className="table-container partner-settlement-table-container">
                <table className="supplier-selection-table sortable-table">
                    <TableHeader
                        isSupplierUser={isSupplierUser}
                        sortConfig={sortConfig}
                        handleSort={handleSort}
                    />
                    <tbody>
                        {!loading && sortedSettlements.length === 0 ? (
                            <tr>
                                <td colSpan={23} className="text-center text-gray-600">
                                    조회된 정산내역이 없습니다.
                                </td>
                            </tr>
                        ) : (
                            currentItems.map((settlement, index) => (
                                <SettlementTableRow
                                    key={settlement.no}
                                    settlement={settlement}
                                    index={index}
                                    currentPage={currentPage}
                                    isSupplierUser={isSupplierUser}
                                />
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* 페이지네이션 */}
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={goToPage}
            />
        </div>
    );
};

export default PartnerSettlementHistory;
